import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { SECRETS_ENV } from '../../../../Config/firebase';
import { LiveKitRoom as LiveKitRoomComponent } from '../SubscriptionDetails/Components/LiveKitRoom';

const API_ENDPOINT = SECRETS_ENV.API_END_POINT;

const LIVEKIT_SERVER_URL = 'wss://hosaksham-7o3jzavj.livekit.cloud';

export default function GuestJoin() {
    const [searchParams] = useSearchParams();
    
    const [guestName, setGuestName] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [meetingToken, setMeetingToken] = useState('');
    const [meetingInfo, setMeetingInfo] = useState<{
        roomName: string;
        meetingTitle: string;
        subscriptionName?: string;
    } | null>(null);
    const [showThankYou, setShowThankYou] = useState(false);
    const [disconnectReason, setDisconnectReason] = useState<'ended' | 'disconnected' | null>(null);
    
    // Feedback state
    const [showFeedback, setShowFeedback] = useState(true);
    const [rating, setRating] = useState<number>(0);
    const [hoveredRating, setHoveredRating] = useState<number>(0);
    const [feedbackText, setFeedbackText] = useState('');
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

    const roomName = searchParams.get('room');
    const meetingTitle = searchParams.get('title') || 'Meeting';
    const subscriptionName = searchParams.get('subscription');
    const tenantId = searchParams.get('tenant');

    useEffect(() => {
        if (!roomName || !tenantId) {
            toast.error('Invalid meeting link');
        }
    }, [roomName, tenantId]);

    const handleJoin = async () => {
        if (!guestName.trim()) {
            toast.error('Please enter your name');
            return;
        }

        if (!roomName || !tenantId) {
            toast.error('Invalid meeting link');
            return;
        }

        try {
            setIsJoining(true);
            
            // Generate guest token with explicit permissions
            const response = await axios.post(`${API_ENDPOINT}/api/meet/generateToken`, {
                roomName: roomName,
                participantName: guestName.trim(),
                options: {
                    tenantId: tenantId,
                    userId: `guest_${Date.now()}`,
                    role: 'student', // Guests are always students
                    canPublish: true, // Allow guests to publish audio/video
                    canSubscribe: true, // Allow guests to view others
                    canPublishData: true, // Allow guests to send chat messages
                    canUpdateOwnMetadata: true, // Required for puzzle sharing
                    metadata: {
                        subscriptionName: subscriptionName || '',
                        joinedAt: new Date().toISOString(),
                        meetingTitle: meetingTitle,
                        isGuest: true,
                        role: 'student' // Include role in metadata for easy access
                    }
                }
            });

            const { token } = response.data;

            if (!token) {
                throw new Error('No token received from server');
            }

            // Set meeting info to render LiveKit component
            setMeetingInfo({
                roomName,
                meetingTitle,
                subscriptionName: subscriptionName || undefined
            });
            setMeetingToken(token);

            toast.success('Joining meeting...');

        } catch (error: any) {
            console.error('Error joining meeting:', error);
            toast.error(error.response?.data?.message || 'Failed to join meeting');
        } finally {
            setIsJoining(false);
        }
    };

    const handleDisconnect = (reason: 'ended' | 'disconnected' = 'ended') => {
        setMeetingToken('');
        setDisconnectReason(reason);
        setShowThankYou(true);
        toast.success(reason === 'ended' ? 'Meeting ended' : 'Disconnected from meeting');
    };

    const handleRejoin = () => {
        setShowThankYou(false);
        setDisconnectReason(null);
        // Keep the guest name and meeting info, just rejoin
        handleJoin();
    };

    const handleSubmitFeedback = async () => {
        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }

        try {
            setIsSubmittingFeedback(true);

            // Send feedback to backend API
            const payload: { rating: number; feedback: string; roomName?: string; eventId?: string } = {
                rating: rating,
                feedback: (feedbackText || '').trim()
            };

            if (roomName) {
                payload.roomName = roomName;
                payload.eventId = roomName; // Use roomName as eventId when available
            }

            await axios.post(`${API_ENDPOINT}/api/meet/meetingFeedback`, payload);

            setFeedbackSubmitted(true);
            setShowFeedback(false);
            toast.success('Thank you for your feedback!');

        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback. Please try again.');
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const handleSkipFeedback = () => {
        setShowFeedback(false);
        toast('Feedback skipped', { icon: 'ℹ️' });
    };

    // If already in meeting, show LiveKit component
    if (meetingInfo && meetingToken && !showThankYou) {
        return (
            <LiveKitRoomComponent
                roomName={meetingInfo.roomName}
                meetingTitle={meetingInfo.meetingTitle}
                token={meetingToken}
                serverUrl={LIVEKIT_SERVER_URL}
                participantName={guestName}
                userRole="student"
                tenantId={tenantId || ''}
                subscriptionId={roomName}
                subscriptionName={meetingInfo.subscriptionName || ''}
                onDisconnect={() => handleDisconnect('ended')}
                onError={(error) => {
                    console.error('LiveKit room error:', error);
                    toast.error('Meeting connection error');
                    handleDisconnect('disconnected');
                }}
            />
        );
    }

    // Show Thank You page after meeting ends
    if (showThankYou) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center p-4">
                <div className="max-w-2xl w-full">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-6 text-center">
                        {/* Logo */}
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M23 7l-7 5 7 5V7z"></path>
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                                Hosaksham Meet+
                            </h1>
                        </div>

                        {/* Thank You Message */}
                        <div className="space-y-4">
                            {disconnectReason === 'disconnected' ? (
                                <>
                                    <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="8" x2="12" y2="12"></line>
                                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                        </svg>
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-900">
                                        Connection Lost
                                    </h2>
                                    <p className="text-lg text-gray-600">
                                        You were disconnected from the meeting
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        This could be due to network issues or the meeting ending
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-900">
                                        Thank You for Joining!
                                    </h2>
                                    <p className="text-lg text-gray-600">
                                        The meeting has ended
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {meetingTitle}
                                    </p>
                                </>
                            )}
                        </div>

                        {/* Meeting Info */}
                        <div className="bg-gray-50 rounded-lg p-4 text-left">
                            <h3 className="font-semibold text-gray-900 mb-2 text-sm">Meeting Details</h3>
                            <div className="space-y-1 text-sm text-gray-600">
                                <p><span className="font-medium">Participant:</span> {guestName}</p>
                                <p><span className="font-medium">Meeting:</span> {meetingTitle}</p>
                                {subscriptionName && (
                                    <p><span className="font-medium">Subscription:</span> {subscriptionName}</p>
                                )}
                            </div>
                        </div>

                        {/* Feedback Section */}
                        {showFeedback && !feedbackSubmitted && (
                            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-5 border border-purple-200">
                                <h3 className="font-semibold text-gray-900 mb-3 text-center">How was your experience?</h3>
                                
                                {/* Star Rating */}
                                <div className="flex justify-center gap-2 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRating(star)}
                                            onMouseEnter={() => setHoveredRating(star)}
                                            onMouseLeave={() => setHoveredRating(0)}
                                            className="transition-transform hover:scale-110 focus:outline-none"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="32"
                                                height="32"
                                                viewBox="0 0 24 24"
                                                fill={star <= (hoveredRating || rating) ? '#fbbf24' : 'none'}
                                                stroke={star <= (hoveredRating || rating) ? '#fbbf24' : '#d1d5db'}
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                                
                                {rating > 0 && (
                                    <p className="text-center text-sm text-gray-600 mb-3">
                                        {rating === 5 && '⭐ Excellent!'}
                                        {rating === 4 && '👍 Great!'}
                                        {rating === 3 && '😊 Good'}
                                        {rating === 2 && '😐 Okay'}
                                        {rating === 1 && '😞 Needs improvement'}
                                    </p>
                                )}

                                {/* Feedback Text */}
                                <div className="mb-4">
                                    <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                                        Share your thoughts (optional)
                                    </label>
                                    <textarea
                                        id="feedback"
                                        value={feedbackText}
                                        onChange={(e) => setFeedbackText(e.target.value)}
                                        placeholder="What did you like or what could be improved?"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                                        rows={3}
                                        maxLength={500}
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {feedbackText.length}/500
                                    </p>
                                </div>

                                {/* Feedback Buttons */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSubmitFeedback}
                                        disabled={isSubmittingFeedback || rating === 0}
                                        className="flex-1 py-2 px-4 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                    >
                                        {isSubmittingFeedback ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Submitting...
                                            </span>
                                        ) : (
                                            'Submit Feedback'
                                        )}
                                    </button>
                                    <button
                                        onClick={handleSkipFeedback}
                                        disabled={isSubmittingFeedback}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors text-sm disabled:opacity-50"
                                    >
                                        Skip
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Feedback Submitted Message */}
                        {feedbackSubmitted && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                </div>
                                <p className="text-green-800 font-semibold">Feedback Submitted!</p>
                                <p className="text-sm text-green-600 mt-1">Thank you for helping us improve</p>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="space-y-3 pt-4">
                            <button
                                onClick={handleRejoin}
                                className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10"></polyline>
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                </svg>
                                <span>Rejoin Meeting</span>
                            </button>
                            
                            <button
                                onClick={() => window.close()}
                                className="w-full py-3 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Close Window
                            </button>
                        </div>

                        {/* Footer */}
                        <div className="pt-6 border-t border-gray-200">
                            <p className="text-sm text-gray-500">
                                Powered by <span className="font-semibold text-purple-600">Hosaksham Meet+</span>
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                                Experience the future of virtual meetings
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center p-4">
            <div className="max-w-6xl w-full grid md:grid-cols-2 gap-8 items-center">
                {/* Left Side - Branding & Marketing */}
                <div className="space-y-6 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start">
                        <img 
                            src="https://firebasestorage.googleapis.com/v0/b/saksham-e4b33.appspot.com/o/assets%2FHosakshamHorizontal.png?alt=media&token=c4337857-7ec0-49a1-8e73-3c46e23dd925" 
                            alt="Hosaksham" 
                            className="h-16 md:h-20 object-contain"
                            onError={(e) => {
                                // Fallback to text logo
                                e.currentTarget.style.display = 'none';
                                const parent = e.currentTarget.parentElement;
                                if (parent) {
                                    parent.innerHTML = '<h1 class="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Hosaksham</h1>';
                                }
                            }}
                        />
                    </div>
                    
                    <div className="space-y-4">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Welcome to Meet+
                        </h2>
                        <p className="text-xl text-gray-600">
                            A futuristic meeting experience
                        </p>
                    </div>

                    <div className="space-y-4 text-left bg-white/60 backdrop-blur rounded-2xl p-6">
                        <h3 className="font-semibold text-gray-900 text-lg mb-3">✨ What makes Meet+ special?</h3>
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                                    1
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">Crystal Clear HD Video</h4>
                                    <p className="text-sm text-gray-600">Experience meetings in stunning high definition</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                                    2
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">Interactive Whiteboard</h4>
                                    <p className="text-sm text-gray-600">Collaborate in real-time with built-in tools</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                                    3
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">Seamless Experience</h4>
                                    <p className="text-sm text-gray-600">No downloads, works right in your browser</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                                    4
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">Recording & Playback</h4>
                                    <p className="text-sm text-gray-600">Never miss a moment with automatic recording</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-center md:text-left">
                        <p className="text-sm text-gray-500">
                            Powered by Hosaksham's cutting-edge technology
                        </p>
                    </div>
                </div>

                {/* Right Side - Join Form */}
                <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-6">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 7l-7 5 7 5V7z"></path>
                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Join Meeting</h3>
                        <p className="text-gray-600">{meetingTitle}</p>
                        {subscriptionName && (
                            <p className="text-sm text-gray-500 mt-1">{subscriptionName}</p>
                        )}
                    </div>

                    {!roomName || !tenantId ? (
                        <div className="text-center py-8">
                            <div className="text-red-600 mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-2">Invalid Meeting Link</h4>
                            <p className="text-sm text-gray-600">
                                Please check your meeting link and try again.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="guestName" className="block text-sm font-medium text-gray-700 mb-2">
                                        Your Name *
                                    </label>
                                    <input
                                        id="guestName"
                                        type="text"
                                        value={guestName}
                                        onChange={(e) => setGuestName(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleJoin();
                                            }
                                        }}
                                        placeholder="Enter your full name"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                        disabled={isJoining}
                                        autoFocus
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        This name will be visible to other participants
                                    </p>
                                </div>

                                <button
                                    onClick={handleJoin}
                                    disabled={isJoining || !guestName.trim()}
                                    className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                                >
                                    {isJoining ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Joining...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                            </svg>
                                            <span>Join Meeting</span>
                                        </div>
                                    )}
                                </button>
                            </div>

                            <div className="pt-4 border-t border-gray-200">
                                <div className="flex items-start gap-2 text-xs text-gray-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                    <p>
                                        By joining, you agree to be recorded and to follow the meeting guidelines. 
                                        Please ensure your microphone and camera are ready.
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

